import dayjs from 'dayjs';
import { useState, useMemo } from 'react';

import { useAppSelector } from '@verantos-dev/core-annotation-tool';

import { selectGroupBy } from '../../../state/selector';
import type { Note } from '../../../types/response';

interface GroupedNotes {
  [key: string]: Note[];
}

export interface UseNoteListItemsResult {
  expandedGroups: Set<string>;
  groupedNotes: GroupedNotes | null;
  handleToggleGroup: (groupKey: string) => void;
  groupBy: string;
}

interface UseNoteListItemsProps {
  notes: Note[];
}
export const useNoteListItems = ({ notes }: UseNoteListItemsProps): UseNoteListItemsResult => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const groupBy = useAppSelector(selectGroupBy);
  const groupedNotes = useMemo(() => {
    if (groupBy === 'none') {
      return null;
    }

    const grouped: GroupedNotes = {};

    notes.forEach((note) => {
      let groupKey: string;

      if (groupBy === 'type') {
        groupKey = note.noteType;
      } else if (groupBy === 'date') {
        groupKey = dayjs(note.noteDatetime).format('MM/DD/YYYY');
      } else {
        return;
      }

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(note);
    });

    return grouped;
  }, [notes, groupBy]);

  const handleToggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  return {
    expandedGroups,
    groupedNotes,
    handleToggleGroup,
    groupBy,
  };
};
